
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface StatCounterProps {
  value: number;
  label: string;
  icon: React.ReactNode;
  className?: string;
}

export const StatCounter = ({
  value,
  label,
  icon,
  className,
}: StatCounterProps) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const statRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      setIsVisible(entry.isIntersecting);
    }, {
      threshold: 0.1 // Trigger when at least 10% of the element is visible
    });
    
    if (statRef.current) {
      observer.observe(statRef.current);
    }
    
    return () => {
      if (statRef.current) {
        observer.unobserve(statRef.current);
      }
    };
  }, []);
  
  useEffect(() => {
    if (!isVisible) return;
    
    const duration = 2000; // Animation duration in milliseconds
    const steps = 50; // Number of steps for the animation
    const stepValue = value / steps;
    const stepTime = duration / steps;
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep += 1;
      if (currentStep <= steps) {
        setCount(Math.round(stepValue * currentStep));
      } else {
        setCount(value);
        clearInterval(timer);
      }
    }, stepTime);
    
    return () => clearInterval(timer);
  }, [value, isVisible]);
  
  return (
    <div 
      ref={statRef}
      className={cn(
        "flex flex-col items-center justify-center p-4 transition-all duration-500",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10",
        className
      )}
    >
      <div className="text-primary mb-2">{icon}</div>
      <div className="text-4xl font-bold mb-1">{count.toLocaleString()}</div>
      <div className="text-muted-foreground text-sm">{label}</div>
    </div>
  );
};
